'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCalendarRange, faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { useAnalytics, useAvailableDataRange } from 'src/hooks/analytics';
import { useLogging } from 'src/context/logging';
import { Checkbox } from 'src/components/PulseUI/Checkbox';
import {
  AvailableDataRange,
  Granularity,
  Period,
  QuarterNum,
  TimePreset,
  dedupeAndCapPeriods,
  expandPreset,
  formatPeriodLabel,
  getPeriodKey,
  lastCompleteCalendarYear,
  periodsToYearBounds,
} from 'src/utils/quarterUtils';

interface AnalyticsQuarterTimePickerProps {
  /** Analytics location key; defaults to TRENDS via useAnalytics when omitted. */
  location?: string;
  /**
   * Overrides the available data window (presets + Custom grid). Defaults to
   * `useAvailableDataRange`; used by insights with a narrower window (e.g.
   * Reimbursement) to restrict the selectable periods.
   */
  availableRange?: AvailableDataRange;
  /**
   * Marks `availableRange` as authoritative. When true, an `undefined`
   * `availableRange` keeps the picker in its loading state instead of falling
   * back to the global default window — so a restricted insight (e.g.
   * Reimbursement) never offers unsupported periods while its range loads.
   */
  restrictRange?: boolean;
  className?: string;
}

/**
 * Quarter-aware time picker: a single dropdown combining the "View By"
 * granularity (Quarter | Year) and the "Time Period" selection (presets +
 * Custom grid) to keep the toolbar to one slot. Emits the `granularity` +
 * `periods` contract via Redux and keeps the legacy `date_range` (gte/lte)
 * bridged. Selections apply immediately.
 */
const AnalyticsQuarterTimePicker: React.FC<AnalyticsQuarterTimePickerProps> = ({
  location,
  availableRange: availableRangeProp,
  restrictRange = false,
  className = '',
}) => {
  const log = useLogging();
  const defaultRange = useAvailableDataRange();
  // A restricted insight is bound to its own range — don't fall back to the
  // broader default window while that range is still loading.
  const availableRange = restrictRange
    ? availableRangeProp
    : availableRangeProp ?? defaultRange;
  const {
    granularity = 'quarter',
    periods = [],
    timePreset = 'last4',
    date_range,
    setGranularity,
    setTimePreset,
  } = useAnalytics(location) || {};

  // The available quarters for each year, from `availableRange` (only complete
  // quarters — the Custom grid renders exactly these, not a fixed four).
  const availableQuartersByYear = useMemo(() => {
    const byYear = new Map<number, QuarterNum[]>();
    availableRange?.availableQuarters.forEach((period) => {
      if (!period.quarter) return;
      const quarters = byYear.get(period.year) ?? [];
      quarters.push(period.quarter);
      byYear.set(period.year, quarters);
    });

    return byYear;
  }, [availableRange]);

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  // Optimistic selection shown while a burst of Custom-grid clicks is debounced
  // before committing to Redux (which drives the trends API query). Null when no
  // pending edit (the committed `periods` are authoritative).
  const [pendingPeriods, setPendingPeriods] = useState<Period[] | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const anchorLeftRef = useRef(0);
  const pendingPeriodsRef = useRef<Period[] | null>(null);
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const COMMIT_DEBOUNCE_MS = 500;

  // --- selection handlers ----------------------------------------------------

  const commitPreset = useCallback(
    (preset: Exclude<TimePreset, 'custom'>, gran: Granularity) => {
      if (!availableRange) return;
      // In year mode "Last 4 Years" equals "All available" (only 4 years of
      // data), so it isn't offered — fold any stray 'last4' into 'all'.
      const effectivePreset =
        gran === 'year' && preset === 'last4' ? 'all' : preset;
      const expanded = expandPreset(effectivePreset, gran, availableRange);
      const bounds = periodsToYearBounds(expanded) ?? date_range;
      setTimePreset(effectivePreset, expanded, bounds);
    },
    [availableRange, date_range, setTimePreset]
  );

  const commitCustom = useCallback(
    (nextPeriods: Period[]) => {
      const deduped = dedupeAndCapPeriods(nextPeriods);
      const bounds = periodsToYearBounds(deduped) ?? date_range;
      setTimePreset('custom', deduped, bounds);
    },
    [date_range, setTimePreset]
  );

  const clearPendingTimer = useCallback(() => {
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
  }, []);

  // Show the toggle optimistically, but only commit (→ one API call) after the
  // user stops clicking for COMMIT_DEBOUNCE_MS.
  const scheduleCommit = useCallback(
    (next: Period[]) => {
      pendingPeriodsRef.current = next;
      setPendingPeriods(next);
      clearPendingTimer();
      commitTimerRef.current = setTimeout(() => {
        commitTimerRef.current = null;
        pendingPeriodsRef.current = null;
        setPendingPeriods(null);
        commitCustom(next);
      }, COMMIT_DEBOUNCE_MS);
    },
    [clearPendingTimer, commitCustom]
  );

  // Commit any pending edit immediately (e.g. when the popover closes).
  const flushPending = useCallback(() => {
    clearPendingTimer();
    const next = pendingPeriodsRef.current;
    if (next) {
      pendingPeriodsRef.current = null;
      setPendingPeriods(null);
      commitCustom(next);
    }
  }, [clearPendingTimer, commitCustom]);

  // Discard a pending edit (e.g. when a preset supersedes the custom selection).
  const cancelPending = useCallback(() => {
    clearPendingTimer();
    pendingPeriodsRef.current = null;
    setPendingPeriods(null);
  }, [clearPendingTimer]);

  // Seed the store once the available range has loaded, if no periods are
  // persisted yet.
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current || !availableRange) return;
    didInit.current = true;
    if (!periods.length) {
      commitPreset(
        timePreset === 'custom' ? 'last4' : timePreset,
        granularity
      );
    }
  }, [availableRange, periods.length, timePreset, granularity, commitPreset]);

  const handleGranularityChange = useCallback(
    (next: Granularity) => {
      if (next === granularity) return;
      log.event('Analytics granularity changed', {
        source: 'AnalyticsQuarterTimePicker',
        location,
        granularity: next,
      });
      cancelPending();
      setGranularity(next);

      if (timePreset === 'custom') {
        const years = Array.from(new Set(periods.map((period) => period.year)));
        const converted: Period[] =
          next === 'year'
            ? years.map((year) => ({ year }))
            : years.flatMap((year) =>
                (availableQuartersByYear.get(year) ?? []).map((quarter) => ({
                  year,
                  quarter,
                }))
              );
        commitCustom(converted);
      } else {
        commitPreset(timePreset, next);
      }
    },
    [
      granularity,
      location,
      log,
      periods,
      timePreset,
      setGranularity,
      commitPreset,
      commitCustom,
      cancelPending,
      availableQuartersByYear,
    ]
  );

  const handleSelectPreset = useCallback(
    (preset: Exclude<TimePreset, 'custom'>) => {
      log.event('Analytics time preset changed', {
        source: 'AnalyticsQuarterTimePicker',
        location,
        preset,
        granularity: granularity,
      });
      cancelPending();
      commitPreset(preset, granularity);
    },
    [commitPreset, granularity, location, log, cancelPending]
  );

  const handleSelectCustom = useCallback(() => {
    log.event('Analytics time preset changed', {
      source: 'AnalyticsQuarterTimePicker',
      location,
      preset: 'custom',
      granularity: granularity,
    });
    cancelPending();
    commitCustom(periods);
  }, [commitCustom, granularity, location, log, periods, cancelPending]);

  const handleTogglePeriod = useCallback(
    (period: Period) => {
      // Accumulate against the latest pending edit (synchronous ref) so a rapid
      // burst of clicks builds on each other before the debounced commit.
      const current = pendingPeriodsRef.current ?? periods;
      const key = getPeriodKey(period);
      const exists = current.some((existing) => getPeriodKey(existing) === key);
      const next = exists
        ? current.filter((existing) => getPeriodKey(existing) !== key)
        : [...current, period];
      scheduleCommit(next);
    },
    [periods, scheduleCommit]
  );

  const handleToggleYear = useCallback(
    (year: number, select: boolean) => {
      if (granularity === 'year') {
        handleTogglePeriod({ year });
        return;
      }
      const current = pendingPeriodsRef.current ?? periods;
      const withoutYear = current.filter((period) => period.year !== year);
      const next = select
        ? [
            ...withoutYear,
            ...(availableQuartersByYear.get(year) ?? []).map((quarter) => ({
              year,
              quarter,
            })),
          ]
        : withoutYear;
      scheduleCommit(next);
    },
    [
      granularity,
      periods,
      scheduleCommit,
      handleTogglePeriod,
      availableQuartersByYear,
    ]
  );

  // Every period the Custom grid actually renders for the active granularity —
  // the available quarters of each year, or one entry per available year. Drives
  // Select all / Clear all.
  const allGridPeriods = useMemo(() => {
    if (!availableRange) return [];
    return granularity === 'quarter'
      ? availableRange.availableQuarters
      : availableRange.availableYears.map((year) => ({ year }));
  }, [granularity, availableRange]);

  const handleToggleSelectAll = useCallback(
    (selectAll: boolean) => {
      scheduleCommit(selectAll ? allGridPeriods : []);
    },
    [allGridPeriods, scheduleCommit]
  );

  // --- popover open / positioning -------------------------------------------

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOpen = () => {
    // The picker is unusable until the available range has loaded.
    if (!availableRange) return;
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      anchorLeftRef.current = rect.left;
      setDropdownPosition({ top: rect.bottom + 4, left: rect.left });
    }
    setIsOpen((prev) => !prev);
  };

  // Commit a pending custom edit as soon as the popover closes, and clear any
  // outstanding timer on unmount so it can't fire after teardown.
  useEffect(() => {
    if (!isOpen) flushPending();
  }, [isOpen, flushPending]);

  useEffect(() => clearPendingTimer, [clearPendingTimer]);

  // Keep the popover within the viewport: shift it left if its right edge (which
  // grows when the Custom panel opens) would run off-screen.
  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;
    const margin = 8;
    const menuWidth = dropdownRef.current.getBoundingClientRect().width;
    const maxLeft = window.innerWidth - menuWidth - margin;
    const clampedLeft = Math.max(margin, Math.min(anchorLeftRef.current, maxLeft));
    setDropdownPosition((prev) =>
      prev && prev.left !== clampedLeft ? { ...prev, left: clampedLeft } : prev
    );
  }, [isOpen, timePreset, periods]);

  // --- derived display -------------------------------------------------------

  // Reflect the optimistic pending edit in the grid + trigger summary while the
  // debounced commit is in flight; otherwise the committed selection.
  const effectivePeriods = pendingPeriods ?? periods;
  const selectedKeys = useMemo(
    () => new Set(effectivePeriods.map(getPeriodKey)),
    [effectivePeriods]
  );


  const unit = granularity === 'quarter' ? 'Quarters' : 'Years';
  const allCount = !availableRange
    ? 0
    : granularity === 'quarter'
    ? availableRange.availableQuarters.length
    : availableRange.availableYears.length;
  const lastCompleteYear = availableRange
    ? lastCompleteCalendarYear(availableRange.latestComplete)
    : undefined;

  const presetOptions: { value: Exclude<TimePreset, 'custom'>; label: string }[] =
    [
      // "Last 4 Years" == "All available" in year mode, so offer it for quarters only.
      ...(granularity === 'quarter'
        ? [{ value: 'last4' as const, label: 'Last 4 Complete' }]
        : []),
      { value: 'all' as const, label: `All available (Past ${allCount} ${unit})` },
      {
        value: 'lastCalendarYear' as const,
        label: `Last Calendar Year (${lastCompleteYear})`,
      },
    ];

  const periodSummary = useMemo(() => {
    switch (timePreset) {
      case 'last4':
        return granularity === 'quarter'
          ? 'Last 4 Complete'
          : 'Last 4 Years';
      case 'all':
        return `Past ${allCount} ${unit}`;
      case 'lastCalendarYear':
        return `${lastCompleteYear}`;
      case 'custom':
      default: {
        const sorted = dedupeAndCapPeriods(effectivePeriods);
        if (sorted.length === 0) return 'Select periods';
        if (sorted.length === 1) return formatPeriodLabel(sorted[0]);
        return `${formatPeriodLabel(sorted[0])} – ${formatPeriodLabel(
          sorted[sorted.length - 1]
        )}`;
      }
    }
  }, [
    timePreset,
    granularity,
    effectivePeriods,
    allCount,
    unit,
    lastCompleteYear,
  ]);

  const granularityPrefix =
    granularity === 'quarter' ? 'By Quarter' : 'By Year';

  const isYearFullySelected = (year: number) => {
    if (granularity !== 'quarter') return selectedKeys.has(`${year}`);

    const quarters = availableQuartersByYear.get(year) ?? [];
    return (
      quarters.length > 0 &&
      quarters.every((quarter) => selectedKeys.has(`${year}-Q${quarter}`))
    );
  };

  const granularityOptions: { label: string; value: Granularity }[] = [
    { label: 'By Quarter', value: 'quarter' },
    { label: 'By Year', value: 'year' },
  ];

  return (
    <div
      className={`w-52 shrink-0 inline-flex flex-col justify-start items-start gap-1 relative ${className}`}
      ref={containerRef}
      data-testid="analytics-quarter-time-picker"
    >
      <div
        ref={buttonRef}
        className={`w-full px-4 bg-white rounded-full border border-neutral-200 inline-flex justify-between items-center h-9 ${
          availableRange
            ? 'cursor-pointer hover:bg-neutral-100'
            : 'cursor-default opacity-50'
        }`}
        onClick={toggleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleOpen();
          }
        }}
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-disabled={!availableRange}
        aria-label="Time period selector"
        data-testid="analytics-time-picker-trigger"
      >
        <div className="flex justify-start items-center gap-1.5 flex-1 min-w-0">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <Icon icon={faCalendarRange} className="text-secondary w-3.5 h-3" />
          </div>
          <div className="self-stretch justify-start text-primary text-sm font-medium font-['Inter'] leading-5 line-clamp-1">
            {availableRange
              ? `${granularityPrefix} · ${periodSummary}`
              : 'Loading…'}
          </div>
        </div>
        <Icon
          icon={faCaretDown}
          className={`text-neutral-500 w-4 h-4 ml-2 transition-all duration-300 ${
            isOpen ? '-rotate-90' : 'rotate-0'
          }`}
        />
      </div>

      {isOpen &&
        dropdownPosition &&
        availableRange &&
        createPortal(
          <div
            ref={dropdownRef}
            role="menu"
            aria-label="Time period options"
            className="fixed z-[99999] bg-white border border-neutral-200 rounded-lg shadow-md flex"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
            data-testid="analytics-time-picker-menu"
          >
            {/* Granularity + presets */}
            <div className="w-60 py-2 flex flex-col">
              {/* Granularity segmented control */}
              <div className="px-3 pb-2">
                <div className="grid grid-cols-2 rounded-full border border-neutral-200 relative h-8 bg-white">
                  <div
                    className={`absolute h-full w-1/2 transition-all duration-300 ease-in-out rounded-full bg-sky-600 ${
                      granularity === 'quarter' ? 'left-0' : 'left-1/2'
                    }`}
                  />
                  {granularityOptions.map((option) => {
                    const isActive = granularity === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={isActive}
                        className={`z-10 h-full rounded-full text-xs font-medium transition-colors cursor-pointer ${
                          isActive ? 'text-white' : 'text-secondary'
                        }`}
                        onClick={() => handleGranularityChange(option.value)}
                        data-testid={`granularity-${option.value}`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="border-t border-neutral-200" />

              {/* Period presets */}
              <div className="py-1 flex flex-col">
                {presetOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={timePreset === option.value}
                    className={`text-left px-4 py-2 text-sm hover:bg-neutral-100 ${
                      timePreset === option.value
                        ? 'bg-neutral-100 font-medium text-primary'
                        : 'text-neutral-700'
                    }`}
                    onClick={() => handleSelectPreset(option.value)}
                    data-testid={`time-preset-${option.value}`}
                  >
                    {option.label}
                  </button>
                ))}
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={timePreset === 'custom'}
                  className={`text-left px-4 py-2 text-sm hover:bg-neutral-100 flex items-center justify-between ${
                    timePreset === 'custom'
                      ? 'bg-neutral-100 font-medium text-primary'
                      : 'text-neutral-700'
                  }`}
                  onClick={handleSelectCustom}
                  data-testid="time-preset-custom"
                >
                  <span>Custom</span>
                  <Icon icon={faCaretDown} className="w-2.5 h-2.5 -rotate-90" />
                </button>
              </div>
            </div>

            {/* Custom grid - expands to the right */}
            {timePreset === 'custom' && (
              <div
                className="w-56 border-l border-neutral-200 flex flex-col"
                data-testid="custom-period-grid"
              >
                <div className="py-2 px-3">
                  <p className="text-xs font-bold text-neutral-900 leading-5 mb-2">
                    Select{' '}
                    {granularity === 'quarter' ? 'quarters' : 'years'}
                  </p>
                  {availableRange.availableYears.map((year) => (
                    <div key={year} className="mb-2">
                      <Checkbox
                        size="sm"
                        checked={isYearFullySelected(year)}
                        onChange={() =>
                          handleToggleYear(year, !isYearFullySelected(year))
                        }
                        label={`${year}`}
                        data-testid={`custom-year-${year}`}
                      />
                      {granularity === 'quarter' && (
                        <div className="ml-6 mt-1 grid grid-cols-2 gap-1">
                          {(availableQuartersByYear.get(year) ?? []).map(
                            (quarter) => (
                              <Checkbox
                                key={quarter}
                                size="sm"
                                checked={selectedKeys.has(`${year}-Q${quarter}`)}
                                onChange={() =>
                                  handleTogglePeriod({ year, quarter })
                                }
                                label={`Q${quarter}`}
                                data-testid={`custom-quarter-${year}-Q${quarter}`}
                              />
                            )
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Footer - mirrors the strategy-center selector dropdown */}
                <div className="border-t border-neutral-200 px-4 py-3 flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => handleToggleSelectAll(true)}
                    className="text-sm font-semibold text-primary text-left hover:underline"
                    aria-label="Select all periods"
                    data-testid="custom-select-all"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleSelectAll(false)}
                    className="text-sm text-tertiary text-left hover:underline"
                    aria-label="Clear period selection"
                    data-testid="custom-clear-all"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default AnalyticsQuarterTimePicker;
